import { useMutation } from "@apollo/client/react";
import { DSBadge } from "@components/design-system/molecules";
import {
  faExclamationCircle,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mixpanel from "mixpanel-browser";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { RETRY_FAILED_AUTO_SOAP } from "../../../graphql/mutations/RetryFailedAutoSoap";
import { alert } from "../../common/Alert";

export const getNoteStatusList = (note) => {
  const noteStatuses = [];

  if (note?.isNewRecording && !note?.isMarkedAsProcessing) {
    noteStatuses.push("new");
  } else if (
    note?.isMarkedAsReview &&
    !note?.isApproved &&
    !note?.isMarkedAsFailed &&
    !note?.isUnacceptable
  ) {
    noteStatuses.push("review");
  }
  if (note?.isDraft && !note?.isMarkedAsProcessing) {
    noteStatuses.push("draft");
  }
  if (note?.hasBeenExported) {
    noteStatuses.push("exported");
  }
  if (note?.isUnacceptable && !note?.isApproved) {
    noteStatuses.push("failed");
  }
  if (note?.isTagged && note?.isApproved) {
    noteStatuses.push("completed");
  }
  if (note?.isMarkedAsProcessing) {
    noteStatuses.push("processing");
  }
  if (note?.isMarkedAsFailed && !note?.isIncomplete) {
    noteStatuses.push("retry");
  }
  if (note?.isIncomplete) {
    noteStatuses.push("incomplete");
  }
  return noteStatuses;
};

export default function NoteStatus({ note }) {
  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);
  const [isHovering, setIsHovering] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const handleRetryFailedAutoSoap = async (event) => {
    event.stopPropagation();
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: note?.uuid,
          jobType: note?.jobType,
          templateUuid: note?.autoTemplates?.uuid,
        },
      });
      alert("warning", "Retrying failed Automatic Note...");
      mixpanel.track("Note reprocessed", {
        noteUuid: note?.uuid,
        jobType: note?.jobType,
        templateUuid: note?.autoTemplates?.uuid,
        source: "NoteStatus component",
      });
      if (location.pathname != "/dashboard") {
        navigate(
          location.pathname.includes("/pip/")
            ? "/pip/notes/"
            : "/dashboard",
        );
      }
    } catch (e) {
      mixpanel.track("Failed to reprocess note", {
        noteUuid: note?.uuid,
        jobType: note?.jobType,
        templateUuid: note?.autoTemplates?.uuid,
        source: "NoteStatus component",
        error: e.message,
      });
      alert("error", e.message);
    }
  };
  const noteStatuses = getNoteStatusList(note);

  return (
    <div className="flex flex-row items-center space-x-1">
      {noteStatuses.includes("new") && (
        <DSBadge
          variant="cyan"
          size="sm"
          dataTestId="note-new-recording"
        >
          NEW RECORDING
        </DSBadge>
      )}
      {noteStatuses.includes("review") && (
        <DSBadge
          variant="green"
          size="sm"
          dataTestId="note-ready-for-review"
        >
          REVIEW
        </DSBadge>
      )}
      {noteStatuses.includes("draft") && (
        <DSBadge variant="brand" size="sm" dataTestId="note-draft">
          DRAFT
        </DSBadge>
      )}
      {noteStatuses.includes("exported") && (
        <DSBadge variant="blue" size="sm" dataTestId="note-exported">
          EXPORTED
        </DSBadge>
      )}
      {noteStatuses.includes("failed") && (
        <DSBadge
          variant="red"
          size="sm"
          icon={faExclamationCircle}
          dataTestId="note-failed"
        >
          FAILED
        </DSBadge>
      )}
      {noteStatuses.includes("completed") && (
        <DSBadge
          variant="green"
          size="sm"
          dataTestId="note-completed"
        >
          COMPLETED
        </DSBadge>
      )}
      {noteStatuses.includes("processing") && (
        <div
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={handleRetryFailedAutoSoap}
          title={"Processing taking too long? Click to retry."}
          className={
            "hover:cursor-pointer hover:bg-red-500 hover:border-red-500 hover:text-white px-2 py-1 text-[10px] border border-yellow-500 text-yellow-500 rounded-full self-start transition-all"
          }
          data-testid="processingStatus"
        >
          {isHovering ? "RETRY" : "PROCESSING"}
        </div>
      )}
      {noteStatuses.includes("retry") && (
        <div
          onClick={handleRetryFailedAutoSoap}
          title="This recording could not be processed into a note. Click to retry."
          className="px-2 py-1 text-xs shadow-xs hover:shadow-none rounded-full self-start bg-red-500 hover:bg-red-600 text-white hover:cursor-pointer transition-all flex items-center gap-1"
          data-testid="note-failed-retry"
        >
          RETRY <FontAwesomeIcon icon={faRedo} />
        </div>
      )}
      {noteStatuses.includes("incomplete") && (
        <div title="This recording may be incomplete. Click for details.">
          <FontAwesomeIcon
            size="1x"
            className="text-yellow-400 hover:cursor-help"
            icon={faExclamationCircle}
          />
        </div>
      )}
    </div>
  );
}
